import {
  getCustomerCompanyListQueryKey,
  useCustomerCompanyCreate,
  useCustomerCompanyPartialUpdate,
} from "@boundless/bl-api-client/customer/services"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { FormInput } from "@/components/form-elements/FormInput"
import { OnboardingFormStep } from "@/components/onboarding/OnboardingFormStep"
import { type ErrorType } from "@/lib/clients"
import { applyErrorsToForm, type ErrorResponseType } from "@/lib/forms/utils"
import { store } from "@/lib/store"

const schema = z.object({
  legalName: z.string().trim().min(1, "Enter your business name"),
})

type Values = z.infer<typeof schema>

interface Props {
  /**
   * What the field starts with: the name already given, when resuming an
   * abandoned wizard, or the draft left behind by walking back to the welcome.
   */
  defaultName: string
  /** The company this run owns, if one exists yet. */
  companyUuid: string | null
  onSaved: (company: { uuid: string; legalName: string }) => void
  /** Handed whatever is in the field, so a name typed but not saved survives. */
  onBack: (draft: string) => void
}

/**
 * Names the business, and turns that name into a real Company.
 *
 * Plain typed text, no lookup: matching against a registry depends on data
 * that doesn't exist until the State Registry Integration epic.
 *
 * Creating the company here rather than at the end means an abandoned wizard
 * still leaves a real lead behind, and everything after this step has a
 * business to attach to. It renames the company we already have rather than
 * adding another, so going back, or abandoning and returning, never leaves a
 * stray business — at two companies the wizard stops offering itself, which
 * would strand the borrower.
 */
export function BusinessNameStep({
  defaultName,
  companyUuid,
  onSaved,
  onBack,
}: Props) {
  const queryClient = useQueryClient()
  const { mutateAsync: createCompany } = useCustomerCompanyCreate()
  const { mutateAsync: updateCompany } = useCustomerCompanyPartialUpdate()

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { legalName: defaultName },
  })

  const submit = form.handleSubmit(async ({ legalName }) => {
    try {
      const company = companyUuid
        ? await updateCompany({ uuid: companyUuid, data: { legalName } })
        : await createCompany({ data: { legalName } })

      store.setState({ activeCompanyUuid: company.uuid })
      await queryClient.invalidateQueries({
        queryKey: getCustomerCompanyListQueryKey(),
      })
      onSaved({ uuid: company.uuid, legalName: company.legalName })
    } catch (error) {
      // A name the server rejects lands under the name field; anything it
      // cannot attribute falls through to root, which the step renders at the
      // foot. Telling someone to try again when the name itself is the problem
      // just sends them round the same loop.
      applyErrorsToForm(form.setError, error as ErrorType<ErrorResponseType>)
    }
  })

  return (
    <OnboardingFormStep
      form={form}
      onSubmit={submit}
      title="What’s your business called?"
      description="The name your business is registered under."
      label="Continue"
      // Raw value, straight out of the field: going back is not a submission,
      // so an empty or unsaved name is kept exactly as the borrower left it.
      onBack={() => onBack(form.getValues().legalName)}
    >
      <FormInput
        name="legalName"
        label="Legal Business Name"
        placeholder="Legal Business Name"
        inputClassName="h-12"
      />
    </OnboardingFormStep>
  )
}
